/**
 * What a position reading is worth.
 *
 * The phone always answers. It never says "I do not know" — it says "here,
 * give or take sixty metres", and the difference between that and "here, give
 * or take five" is the difference between a usable milestone and a lie.
 *
 * The browser hands over that margin; a photo's own metadata does not. That is
 * the single reason this tool exists instead of just using the camera.
 *
 * Here live only the rules about **one** number: how tight it claims to be and
 * how old it is. Putting several readings together, and the harder question of
 * whether the phone is telling the truth at all, is `position.ts`.
 */

export type FixQuality = "good" | "fair" | "bad";

/**
 * Good enough to say "the milestone is exactly here".
 *
 * Ten metres is roughly the best a phone does in the open, and a milestone is
 * the size of a person: below that the point is worth publishing on its own.
 */
const GOOD_ACCURACY_M = 10;
/** Beyond this the point still locates a stretch of path, but not an object. */
const FAIR_ACCURACY_M = 25;

/**
 * How stale a reading may be and still describe where you are standing.
 *
 * Walking is about 1.4 m/s, so fifteen seconds is twenty metres of drift — the
 * same order as the accuracy limit above. A minute is already a bend away.
 */
const GOOD_AGE_S = 15;
const FAIR_AGE_S = 60;

/**
 * How much to trust a reading, given its margin and how old it is.
 *
 * Both matter and the worse one decides: a five-metre reading from two minutes
 * ago is not a five-metre reading any more.
 */
export function fixQuality(accuracyM: number, ageS: number): FixQuality {
  if (!Number.isFinite(accuracyM) || !Number.isFinite(ageS)) return "bad";
  if (accuracyM <= GOOD_ACCURACY_M && ageS <= GOOD_AGE_S) return "good";
  if (accuracyM <= FAIR_ACCURACY_M && ageS <= FAIR_AGE_S) return "fair";
  return "bad";
}

/**
 * The margin as it gets shown and stored, always rounded up.
 *
 * Rounding 10.4 down to 10 would turn a "fair" reading into a "good" one by
 * arithmetic alone. Where there is doubt it goes against us, never in favour.
 */
export function reportedAccuracyM(accuracyM: number): number {
  return Number.isFinite(accuracyM) ? Math.ceil(accuracyM) : 0;
}
